import { Gauge } from 'lucide-react';
import { OutboundLink } from './LeavingDialog';

/**
 * A speed test, in the top bar, on every screen.
 *
 * ## Why a third party rather than our own
 *
 * An ISP measuring its own customers' speed with its own tool answers the
 * wrong question. When somebody says "I am only getting 8 Mbps", what settles
 * it is a number from a measurement nobody here controls — Ookla's Speedtest
 * is the one customers already know, already trust, and already quote at us.
 * A figure from a GoSpeedNet-branded tester is the figure a doubtful customer
 * discounts, however honest it is.
 *
 * It also avoids reimplementing something genuinely hard. A trustworthy
 * throughput measurement needs geographically distributed servers with known
 * headroom; anything less measures our own uplink to our own box and calls it
 * the internet.
 *
 * ## Why it opens a tab rather than embedding
 *
 * The console's Content-Security-Policy has no `frame-src`, so it falls back
 * to `default-src 'self'` and third-party frames are refused. That could be
 * widened, and deliberately is not: an embedded frame inside the
 * authenticated console is a surface we do not control on the page where
 * sessions live, and Ookla's own embeddable product is a paid one with
 * different terms. Opening the real page in a new tab gives the technician
 * exactly the tool in the screenshot, with no policy loosened and nothing to
 * keep working when somebody else redesigns their site.
 *
 * The leaving dialog handles the click, as it does for every outbound link
 * here: it shows the address in full first, which is the habit that makes a
 * bad link in a customer note survivable.
 */
export const SPEED_TEST_URL = 'https://www.speedtest.net/';

export function SpeedTest() {
  return (
    <OutboundLink
      href={SPEED_TEST_URL}
      title="Run a speed test (opens Speedtest by Ookla)"
      className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <Gauge className="h-5 w-5" aria-hidden="true" />
      <span className="sr-only">Run a speed test</span>
    </OutboundLink>
  );
}
